<template>
    <div id="order-search-form">
        <form v-if="form"
              class="search-page-form form form-v form--on-dark"
              @submit.prevent="submit">
            <input id="order-search-input"
                   ref="order-search-input"
                   v-model="form.search"
                   v-auto-focus
                   v-select-on-focus
                   type="text"
                   class="form-control input-lg search-page-form__input"
                   :placeholder="searchInputPlaceholder"
                   @keyup.enter="submit"
                   @keydown="onKeyDown" />

            <ExpandableFormGroup v-model="expanded.notes"
                                 label="ORDERS.NOTES_LABEL"
                                 :force-expanded="form.notes.length > 0"
                                 class="desktop-only"
                                 @expanded="$refs['notes-input'].focus()">
                <div class="input-group input-group-lg">
                    <input ref="notes-input"
                           v-model="form.notes"
                           type="text"
                           :placeholder="notesInputPlaceholder"
                           class="form-control" />

                    <span class="input-group-btn">
                        <a class="btn btn-danger"
                           @click="form.notes = ''"><i class="fa-solid fa-trash-can"></i></a>
                    </span>
                </div>
            </ExpandableFormGroup>

            <ExpandableFormGroup v-model="expanded.orderDate"
                                 label="ORDERS.ORDER_DATE_LABEL"
                                 :force-expanded="!!form.orderDateStart || !!form.orderDateEnd"
                                 class="desktop-only"
                                 @expanded="$refs['order-date-start-date-pick'].open()">
                <div class="input-group-flex input-group-flex--no-gap">
                    <DtDatePick ref="order-date-start-date-pick"
                                v-model="form.orderDateStart"
                                class="input-group-flex__input dt-date-pick"
                                :input-attributes="{
                                    class: 'form-control input-lg brr-0',
                                    placeholder: startDateInputPlaceholder
                                }" />
                    <a class="btn btn-danger"
                       @click.prevent="form.orderDateStart = null">
                        <i class="fa-solid fa-trash-can"></i>
                    </a>
                </div>

                <div class="input-group-flex input-group-flex--no-gap">
                    <DtDatePick v-model="form.orderDateEnd"
                                class="input-group-flex__input dt-date-pick"
                                :input-attributes="{
                                    class: 'form-control input-lg brr-0',
                                    placeholder: endDateInputPlaceholder
                                }" />
                    <a class="btn btn-danger"
                       @click.prevent="form.orderDateEnd = null">
                        <i class="fa-solid fa-trash-can"></i>
                    </a>
                </div>
            </ExpandableFormGroup>

            <ExpandableFormGroup v-model="expanded.sessionDate"
                                 label="ORDERS.SESSION_DATE_LABEL"
                                 :force-expanded="!!form.sessionDateStart || !!form.sessionDateEnd"
                                 class="desktop-only"
                                 @expanded="$refs['session-date-start-date-pick'].open()">
                <div class="input-group-flex input-group-flex--no-gap">
                    <DtDatePick ref="session-date-start-date-pick"
                                v-model="form.sessionDateStart"
                                class="input-group-flex__input dt-date-pick"
                                :input-attributes="{
                                    class: 'form-control input-lg brr-0',
                                    placeholder: startDateInputPlaceholder
                                }" />
                    <a class="btn btn-danger"
                       @click.prevent="form.sessionDateStart = null">
                        <i class="fa-solid fa-trash-can"></i>
                    </a>
                </div>

                <div class="input-group-flex input-group-flex--no-gap">
                    <DtDatePick v-model="form.sessionDateEnd"
                                class="input-group-flex__input dt-date-pick"
                                :input-attributes="{
                                    class: 'form-control input-lg brr-0',
                                    placeholder: endDateInputPlaceholder
                                }" />
                    <a class="btn btn-danger"
                       @click.prevent="form.sessionDateEnd = null">
                        <i class="fa-solid fa-trash-can"></i>
                    </a>
                </div>
            </ExpandableFormGroup>

            <!-- Session - only shown if a session is added to the form data, and cannot be collapsed. -->
            <ExpandableFormGroup v-if="form.sessionWithEvent"
                                 v-model="expanded.session"
                                 label="ORDERS.SESSION_LABEL"
                                 :force-expanded="!!form.sessionWithEvent"
                                 class="desktop-only">
                <p class="order-search-form__session">
                    <span class="order-search-form__session-name">{{ form.sessionWithEvent.event.name }}</span>
                    <span class="order-search-form__session-time">{{ sessionFullName }}</span>
                </p>
            </ExpandableFormGroup>

            <ExpandableFormGroup v-model="expanded.options"
                                 label="ORDERS.SEARCH_OPTIONS_LABEL"
                                 :force-expanded="forceExpandOptions"
                                 class="desktop-only text-lightly-muted">
                <Toggle v-model="form.includeCancelled"
                        :disabled="lockCancelledOption"
                        label="ORDERS.OPTION_INCLUDE_CANCELLED"
                        label-position="before"
                        name="includeCancelled"
                        class="toggle--text-left toggle--borderless"
                        @click="onCancelledToggleClick"
                        @change="onCancelledToggleChange" />

                <Toggle v-model="form.includeUnconfirmed"
                        :disabled="lockUnconfirmedOption"
                        label="ORDERS.OPTION_INCLUDE_UNCONFIRMED"
                        label-position="before"
                        name="includeUnconfirmed"
                        class="mt-1 toggle--text-left toggle--borderless mt-15"
                        @click="onUnconfirmedToggleClick"
                        @change="onUnconfirmedToggleChange" />

                <Toggle v-model="form.groupBookingsOnly"
                        label="ORDERS.OPTION_GROUP_BOOKINGS_ONLY"
                        label-position="before"
                        name="groupBookingsOnly"
                        class="mt-1 toggle--text-left toggle--borderless mt-15"
                        @change="onGroupBookingsOnlyToggleChange" />
            </ExpandableFormGroup>

            <hr class="mt-2 mb-2" />

            <div class="flex flex--gap">
                <button type="button"
                        class="search-page-form__clear-btn btn btn-xl btn-default flex--no-grow"
                        :disabled="!clearButtonEnabled"
                        @click="clear()">
                    <i class="fa-solid fa-circle-xmark visible-xs"></i>
                    <span>{{ l('ORDERS.CLEAR') }}</span>
                </button>
                <button type="submit"
                        :disabled="!isValidSearch"
                        class="search-page-form__search-btn btn btn-xl btn-primary flex--grow">
                    <span>{{ l('ORDERS.SEARCH_BTN') }}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import DtDatePick from '@/vue/components/DatePicker/DtDatePick';
import ExpandableFormGroup from '@/vue/components/Forms/ExpandableFormGroup';
import LocalStorageKeys from '@/lib/Storage/LocalStorageKeys';
import Toggle from '@/vue/components/Forms/Toggle';
import { autoFocus } from '@/vue/directives/autoFocus';
import { blurElement, focusElement, selectTextInElement } from '@/functions/dom';
import { cloneDeep } from '@/functions/clone';
import { container } from '@/container';
import { createOrderSearchCriteria, updateOrderSearchCriteria } from '@/lib/Orders/Search';
import { selectOnFocus } from '@/vue/directives/selectOnFocus';

export default {
    name: 'OrderSearchForm',

    components: {
        Toggle,
        ExpandableFormGroup,
        DtDatePick,
    },

    directives: {
        autoFocus,
        selectOnFocus,
    },

    props: {
        hasResults: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'clear',
        'submit',
    ],

    data() {
        return {
            minQueryLength: 3,

            /**
             * @type {OrderSearchCriteria}
             */
            form: null,

            // Which collapsible form groups have been manually expanded.
            // Note that the group will always be expanded if it has a value.
            expanded: {
                notes: false,
                orderDate: false,
                sessionDate: false,
                session: false,
                options: false,
            },
        };
    },

    computed: {
        searchInputPlaceholder() {
            return container.langService.getText('ORDERS.SEARCH_PLACEHOLDER');
        },

        notesInputPlaceholder() {
            return container.langService.getText('ORDERS.NOTES_PLACEHOLDER');
        },

        startDateInputPlaceholder() {
            return container.langService.getText('ORDERS.START_DATE_PLACEHOLDER');
        },

        endDateInputPlaceholder() {
            return container.langService.getText('ORDERS.END_DATE_PLACEHOLDER');
        },

        isValidSearch() {
            return this.form.searchFormIsValid(this.minQueryLength);
        },

        clearButtonEnabled() {
            return this.form.hasSearchFormValues() || this.hasResults;
        },

        sessionFullName() {
            return this.form.sessionWithEvent ? this.form.sessionWithEvent.session.getFullName() : null;
        },

        lockCancelledOption() {
            // When searching for orders by notes we need to include cancelled orders for performance.
            // When searching for orders by ref we include cancelled orders.
            // This is to reflect that in the UI.
            return ['notes', 'ref'].includes(this.form.includeCancelledReason);
        },

        lockUnconfirmedOption() {
            // When searching for orders by ref we include unconfirmed orders.
            // This is to reflect that in the UI.
            return this.form.includeUnconfirmedReason === 'ref';
        },

        forceExpandOptions() {
            return this.form.includeCancelled || this.form.includeUnconfirmed || this.form.groupBookingsOnly;
        },
    },

    watch: {
        // When things change that could affect the "includeCancelled" setting update the search criteria.
        // Note: this should not watch the entire form, because then the watcher would fire when "includeCancelled"
        // itself changes and cause it to not change correctly.
        'form.search': function () {
            updateOrderSearchCriteria(this.form);
        },
        'form.notes': function () {
            updateOrderSearchCriteria(this.form);
        },
    },

    created() {
        this.form = createOrderSearchCriteria();

        this.setInitialExpandedStates();
    },

    methods: {
        setInitialExpandedStates() {
            // Set the fields that have values as manually expanded.
            // Without doing this the field instantly collapses when you delete the last character,
            // which is not a nice UX.
            if (this.form.notes) {
                this.expanded.notes = true;
            }

            if (this.form.orderDateStart || this.form.orderDateEnd) {
                this.expanded.orderDate = true;
            }

            if (this.form.sessionDateStart || this.form.sessionDateEnd) {
                this.expanded.sessionDate = true;
            }

            if (this.form.includeCancelled) {
                this.expanded.options = true;
            }

            if (this.form.includeUnconfirmed) {
                this.expanded.options = true;
            }

            if (this.form.groupBookingsOnly) {
                this.expanded.options = true;
            }
        },

        /**
         * @param {OrderSearchCriteria} searchCriteria
         */
        setFormData(searchCriteria) {
            this.form.fillFrom(searchCriteria);
            updateOrderSearchCriteria(this.form);
            this.setInitialExpandedStates();
        },

        submit() {
            this.form.wasBarcodeScan = false;

            if (!this.isValidSearch) {
                container.toastFactory.warningTop('MIN_QUERY_LENGTH_ERROR');
                setTimeout(() => {
                    this.focusSearchInput();
                }, 1);
                return;
            }

            // Clone the OrderSearchCriteria.
            const searchCriteria = cloneDeep(this.form);

            // The OrderSearchForm doesn't perform the search. It emits a search event
            // for the parent component to consume.
            this.$emit('submit', searchCriteria);
        },

        /**
         * When the user types something into the search box we know it was no longer a barcode
         * that was scanned, so mark it as such.
         */
        onKeyDown() {
            this.form.wasBarcodeScan = false;
        },

        clear() {
            this.form.clear();

            this.$emit('clear');

            this.focusSearchInput();
        },

        focusSearchInput(onMobile = true, onDesktop = true) {
            focusElement(this.$refs['order-search-input'], onMobile, onDesktop);
            selectTextInElement(this.$refs['order-search-input'], onMobile, onDesktop);
        },

        blurSearchInput(onMobile = true, onDesktop = true) {
            blurElement(this.$refs['order-search-input'], onMobile, onDesktop);
        },

        /**
         * Called when the user changes the "include cancelled" toggle.
         * This does not watch the form.includeCancelled property because that can change programmatically
         * for various reasons, and we don't want to record that as the user's preference. We only save the
         * user's preference to local storage.
         */
        onCancelledToggleChange() {
            setTimeout(() => {
                container.localStorageDataStore.persist(LocalStorageKeys.ORDER_SEARCH_INCLUDE_CANCELLED, this.form.includeCancelled);
            }, 1);
        },

        onCancelledToggleClick() {
            // If the user clicks on the include cancelled toggle while it is disabled
            // show them the reason why it is disabled.
            if (this.lockCancelledOption) {
                switch (this.form.includeCancelledReason) {
                    case 'notes':
                        container.toastFactory.warningTop('ORDERS.OPTION_INCLUDE_CANCELLED--NOTES');
                        break;

                    case 'ref':
                        container.toastFactory.warningTop('ORDERS.OPTION_INCLUDE_CANCELLED--REF');
                        break;
                }
            }
        },

        /**
         * Called when the user changes the "include unconfirmed" toggle.
         * As with the cancelled toggle this does not watch form.includeUnconfirmed, because that can change
         * programmatically, and we only want to record a deliberate choice as the user's preference.
         */
        onUnconfirmedToggleChange() {
            setTimeout(() => {
                container.localStorageDataStore.persist(
                    LocalStorageKeys.ORDER_SEARCH_INCLUDE_UNCONFIRMED,
                    this.form.includeUnconfirmed
                );
            }, 1);
        },

        onUnconfirmedToggleClick() {
            // If the user clicks on the include unconfirmed toggle while it is disabled
            // show them the reason why it is disabled.
            if (this.lockUnconfirmedOption) {
                container.toastFactory.warningTop('ORDERS.OPTION_INCLUDE_UNCONFIRMED--REF');
            }
        },

        /**
         * Persist the value of the groupBookingsOnly toggle in local storage.
         */
        onGroupBookingsOnlyToggleChange() {
            setTimeout(() => {
                container.localStorageDataStore.persist(
                    LocalStorageKeys.ORDER_SEARCH_GROUP_BOOKINGS_ONLY,
                    this.form.groupBookingsOnly
                );
            }, 1);
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../../less/variables";

#order-search-form {
    .form-group {
        p {
            color: @white;
        }
    }
}

.order-search-form__session {
    line-height: normal;
    padding: 8px;
    border-radius: @border-radius-base;
    background: rgba(255, 255, 255, .1);
}

.order-search-form__session-name {
    display: block;
    font-size: @font-size-medium;
}

.order-search-form__session-time {
    display: block;
}
</style>
